import { useMemo, useRef } from "react"

import { Alert, Col, Divider, Flex, Row, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { useLocation } from "react-router-dom"

import Button from "../../Components/Button"
import TitleSection from "../../Components/TitleSection/TitleSection"
import TourComponent from "../../Components/Tour"
import withPermission from "../../hoc/withPermission"
import Icon from "../../Icon"
import { highAccessRoles, onlyOwnerAccess } from "../../Utils/permission"
import BalanceActivity from "../Balance/components/BalanceActivity"
import BusinessDetails from "../BusinessDetailsBlock/BusinessDetails"
import InvoiceHistoryBlock from "../InvoiceHistoryBlock"
import { navToStripe } from "../Setting/redux/setting.thunks"
import { setDemoTourStep } from "../User/redux/user.actions"
import UsageOverview from "./components/UsageOverview/UsageOverview"
import { usePageQuery } from "./usePageQuery"

const { Text } = Typography

const PermissionButton = withPermission(Button, highAccessRoles)

const Billing = ({
  businessAccount,
  ba_role,
  balanceEnabled,
  demoTourStep,
  isAdmin,
  fullInfo,
  personalSetting,
  ...props
}) => {
  const isMobile = useSelector((state) => state.nav.isMobile)
  const userInfoForAdmin = useSelector((state) => state.user.userInfoForAdmin)
  const paymentActivityRef = useRef(null)
  const invoiceHistoryRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { user_id } = usePageQuery(location.search)
  const { t } = useTranslation()
  const businessAccountRole = ba_role
  const showAccountBalanceNotice = !isAdmin && [2, 3, 4].includes(Number(businessAccountRole))
  const isAdminViewingUser = isAdmin && !!user_id
  const ownerFullName = [businessAccount?.owner?.first_name, businessAccount?.owner?.last_name]
    .filter(Boolean)
    .join(" ")
  const adminFullName = [userInfoForAdmin?.first_name, userInfoForAdmin?.last_name]
    .filter(Boolean)
    .join(" ")
  const displayFullName = isAdminViewingUser ? adminFullName : ownerFullName

  const isBaOwner = onlyOwnerAccess.includes(Number(businessAccountRole))
  // The BA-owner title reduction only applies to the owner viewing their own pages; an
  // admin looking up a user always sees the full title, regardless of that user's role.
  const showFullTitle = isAdminViewingUser || !isBaOwner

  const billingTitle = !showFullTitle
    ? "Billing information"
    : businessAccount?.name
      ? displayFullName
        ? `Billing information for ${businessAccount.name} (${displayFullName})`
        : `Billing information for ${businessAccount.name}`
      : displayFullName
        ? `Billing information for ${displayFullName}`
        : "Billing information"

  const handleGoToBilling = () => {
    if (isAdmin) return
    props.goToStripe(location.pathname)
  }

  const steps = useMemo(
    () => [
      {
        title: <Text type="secondary">See all your past payments and transactions here!</Text>,
        target: () => paymentActivityRef?.current
      },
      {
        title: (
          <Text type="secondary">
            Find a complete history of all your invoices in this section.
          </Text>
        ),
        target: () => invoiceHistoryRef?.current,
        nextButtonProps: {
          onClick: () => {
            navigate("/settings/balance")
            props.setDemoTourStep(null)
          }
        }
      }
    ],
    [paymentActivityRef, invoiceHistoryRef]
  )

  return (
    <div style={{ padding: !props.isAdmin ? "24px 32px" : 0 }}>
      {personalSetting && (
        <>
          <TitleSection
            icon="bank"
            stacked={showFullTitle}
            title={billingTitle}
            description="All information about your billing and invoices."
            style={{ margin: isMobile ? "-16px -16px 16px" : "-24px -32px 24px" }}
          />
          {showAccountBalanceNotice && (
            <Col span={24} style={{ marginBottom: 24 }}>
              <Alert
                type="warning"
                className="fullscreen-alert account-context-alert"
                showIcon
                icon={<Icon type="info_bubble" />}
                message={
                  <Text>
                    {t(
                      "Billing information and invoices are related to the account you are currently on."
                    )}
                  </Text>
                }
              />
            </Col>
          )}
        </>
      )}
      {!balanceEnabled ? (
        <>
          {isAdmin ? null : (
            <Row>
              <Col flex={"1 1 300px"}>
                <Space size={8} direction={"vertical"}>
                  <Text strong>{t("Manage")}</Text>
                  <Text type="secondary">
                    {t("Press the button to the right to manage your billing and invoices.")}
                  </Text>
                </Space>
              </Col>
              <Col flex={"0 1 420px"}>
                <Flex
                  gap={12}
                  align={"center"}
                  justify="flex-end"
                  style={{ height: "100%", padding: 3 }}
                >
                  <PermissionButton
                    type={"primary"}
                    onClick={handleGoToBilling}
                    className={"flex"}
                    text={t("Manage Billing")}
                    role={businessAccountRole}
                  >
                    <Icon type="open_link" style={{ fontSize: 20 }} />
                  </PermissionButton>
                </Flex>
              </Col>
            </Row>
          )}
          {!isAdmin && <Divider />}
          <BusinessDetails />
          <Divider />
          <div ref={invoiceHistoryRef}>
            <InvoiceHistoryBlock
              forUser={!isAdmin}
              userViewForAdmin={isAdmin}
              personalSetting={personalSetting}
            />
          </div>
        </>
      ) : (
        <>
          <BusinessDetails />
          <Divider
            style={{
              visibility: isMobile && "hidden",
              margin: isMobile && "12px 0"
            }}
          />

          <UsageOverview />
          <Divider />

          {fullInfo ? (
            <>
              <div ref={paymentActivityRef}>
                <BalanceActivity />
              </div>
              <Divider />
            </>
          ) : null}
          <div ref={invoiceHistoryRef}>
            <InvoiceHistoryBlock
              forUser={!isAdmin}
              userViewForAdmin={isAdmin}
              personalSetting={personalSetting}
            />
          </div>
        </>
      )}
      {isAdmin ? null : (
        <TourComponent
          open={demoTourStep === 8 && balanceEnabled}
          steps={steps}
          onClose={() => props.setDemoTourStep(null)}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  isMobile: state.nav.isMobile,
  isAdmin: state.auth.isAdmin,
  ba_role: state.auth.userInfo?.ba_role?.id,
  businessAccount: state.user.businessAccount,
  balanceEnabled: state.auth.userInfo?.["balance_feature"],
  demoTourStep: state.user.demoTourStep
})

const mapDispatchToProps = (dispatch) => ({
  setDemoTourStep: (step) => dispatch(setDemoTourStep(step)),
  goToStripe: (return_url) => dispatch(navToStripe(return_url))
})

export default connect(mapStateToProps, mapDispatchToProps)(Billing)
